import { DAY_LENGTH_S } from '@sunderfen/shared';
/**
 * Art direction as data. Sky, light, water and foliage colours all come from here,
 * so re-theming the world (say, a walled city in a ruined land) is an edit to this
 * file rather than to the renderer.
 */
export interface SkyKey {
  /** Position in the day, 0..1 (0 = dawn). */
  t: number;
  /** Sun elevation in degrees; below 0 the moon lights the world. */
  elev: number;
  top: number; horizon: number;
  light: number; lightI: number;
  hemiSky: number; hemiGround: number; hemiI: number;
  /** 0 = full day, 1 = deep night (stars, fireflies, glows). */
  night: number;
  /** Mist pooling in low ground, 0..1. */
  mist: number;
}

export interface Look {
  sky: SkyKey[];
  exposure: number;
  water: { shallow: number; deep: number; foam: number };
  /** Flower colours by region key ('*' = everywhere else). */
  flowers: Record<string, number[]>;
  bush: number; reed: number; pebble: number;
  /** Seconds for a full day. */
  dayLength: number;
  /** Strength of drifting cloud shadows by day, 0..1. */
  clouds: number;
  /** The final grade and light shaping, baked into the shaders at startup. */
  grade: Grade;
}

export interface Grade {
  /** Saturation after tone mapping (1 = as rendered). */
  sat: number;
  /** How much of a filmic S-curve to blend in, 0..1. */
  contrast: number;
  /** Light wrapping past the shadow line: high reads soft and toy-like, low reads solid. */
  wrap: number;
  /** Colour pushed into the shadows and into the highlights. */
  shadowTint: [number, number, number];
  highTint: [number, number, number];
  /** Strength of the sky-coloured rim on edges. */
  rim: number;
  /** Distance fog. */
  fogNear: number; fogFar: number;
  /** Ground colours: saturation kept, brightness, a warm/olive cast, and how much broad patchiness. */
  ground: { sat: number; value: number; cast: [number, number, number]; patch: number };
}

export const VALLEY: Look = {
  exposure: 1.15,
  dayLength: DAY_LENGTH_S,
  clouds: 0.32,
  sky: [
    { t: 0.00, elev: 3,   top: 0x4a6a9a, horizon: 0xf0b48c, light: 0xffb07a, lightI: 1.8, hemiSky: 0xbac6e2, hemiGround: 0x6a5a48, hemiI: 1.35, night: 0.15, mist: 0.55 },
    { t: 0.06, elev: 24,  top: 0x5a9ad6, horizon: 0xcfe3ea, light: 0xfff0d6, lightI: 2.6, hemiSky: 0xdbe9ff, hemiGround: 0x6a6a40, hemiI: 1.35, night: 0, mist: 0.28 },
    { t: 0.22, elev: 58,  top: 0x3f8fd8, horizon: 0xc0e2f2, light: 0xfff8ea, lightI: 3.0, hemiSky: 0xe6f0ff, hemiGround: 0x707a48, hemiI: 1.5,  night: 0, mist: 0.04 },
    { t: 0.38, elev: 32,  top: 0x4f93d0, horizon: 0xd8e6e0, light: 0xffe8c0, lightI: 2.7, hemiSky: 0xe0e8f6, hemiGround: 0x786a48, hemiI: 1.4,  night: 0, mist: 0.05 },
    { t: 0.46, elev: 10,  top: 0x5a7fb8, horizon: 0xffc58a, light: 0xffb066, lightI: 2.6, hemiSky: 0xf4dcc4, hemiGround: 0x8a6a48, hemiI: 1.55, night: 0, mist: 0.16 },
    { t: 0.51, elev: 0,   top: 0x33456e, horizon: 0xe88a6a, light: 0xff8a5a, lightI: 1.5, hemiSky: 0xb09ac0, hemiGround: 0x5a4a48, hemiI: 1.2, night: 0.3, mist: 0.3 },
    { t: 0.56, elev: -8,  top: 0x1c2648, horizon: 0x4a5a8a, light: 0x8a9ad8, lightI: 0.8, hemiSky: 0x7a8ac0, hemiGround: 0x30303e, hemiI: 0.95, night: 0.8, mist: 0.42 },
    { t: 0.64, elev: -30, top: 0x0b1026, horizon: 0x1e2a4a, light: 0x9fb4ff, lightI: 0.85, hemiSky: 0x6a7aaa, hemiGround: 0x22222e, hemiI: 0.85, night: 1, mist: 0.36 },
    { t: 0.86, elev: -30, top: 0x0b1026, horizon: 0x1e2a4a, light: 0x9fb4ff, lightI: 0.85, hemiSky: 0x6a7aaa, hemiGround: 0x22222e, hemiI: 0.85, night: 1, mist: 0.36 },
    { t: 0.94, elev: -6,  top: 0x24305a, horizon: 0x6a6a90, light: 0xc0a0c0, lightI: 0.8, hemiSky: 0x8a90c0, hemiGround: 0x32323e, hemiI: 0.95, night: 0.6, mist: 0.52 },
  ],
  water: { shallow: 0x5fd0c4, deep: 0x1d5f86, foam: 0xf2fbff },
  flowers: {
    village: [0xffffff, 0xf6d55c, 0xe58fb0], downs: [0xf6d55c, 0xffffff, 0xb49ae8, 0xf08a5c], birchwood: [0xffffff, 0xf6e9a0],
    lake: [0x8fb8ff, 0xffffff], copperback: [0xf6d55c, 0xf0a05c], shore: [0xffffff], fen: [0xe8e0a0], grove: [0x9ff0e8, 0xffffff], cove: [0xffffff, 0x8ff0e0], '*': [0xffffff, 0xf6d55c],
  },
  bush: 0x3f7a3a, reed: 0x8a9a4a, pebble: 0x9a958a,
  grade: {
    sat: 1.12, contrast: 0.18, wrap: 0.3, shadowTint: [-0.012, 0.004, 0.03], highTint: [0.025, 0.012, -0.02], rim: 1,
    fogNear: 50, fogFar: 125, ground: { sat: 1, value: 1, cast: [1, 1, 1], patch: 0 },
  },
};

/**
 * The moody look: Valheim-like. Muted, earthy colour; a strong warm sun against a weak cool
 * fill so the shadow side of things actually falls into shade; haze that greys and blues the
 * distance; deeper, murkier water; evenings that glow and nights that are properly dark.
 */
export const MOODY: Look = {
  exposure: 1.2,
  dayLength: DAY_LENGTH_S,
  clouds: 0.3,
  sky: [
    { t: 0.00, elev: 3,   top: 0x3a4a66, horizon: 0xc8a08a, light: 0xffa070, lightI: 2.16, hemiSky: 0x8a96b0, hemiGround: 0x3a3228, hemiI: 1.09,  night: 0.15, mist: 0.4 },
    { t: 0.06, elev: 24,  top: 0x5a7896, horizon: 0xb8c4c4, light: 0xffe4c0, lightI: 2.92, hemiSky: 0x9eaec4, hemiGround: 0x3e3c2a, hemiI: 1.03,  night: 0, mist: 0.22 },
    { t: 0.22, elev: 58,  top: 0x52789e, horizon: 0xb0c0c8, light: 0xfff0d8, lightI: 3.24, hemiSky: 0xa8b8cc, hemiGround: 0x423e2c, hemiI: 1.03, night: 0, mist: 0.03 },
    { t: 0.38, elev: 32,  top: 0x56789a, horizon: 0xc0c4be, light: 0xffe0b0, lightI: 3.02, hemiSky: 0xa6b0c0, hemiGround: 0x463c2a, hemiI: 1.03,  night: 0, mist: 0.04 },
    { t: 0.46, elev: 10,  top: 0x4a5e80, horizon: 0xe0a46e, light: 0xff9a50, lightI: 3.46, hemiSky: 0xb09c94, hemiGround: 0x4a3a2a, hemiI: 1.09,  night: 0, mist: 0.12 },
    { t: 0.51, elev: 0,   top: 0x283654, horizon: 0xc0684a, light: 0xff7040, lightI: 1.94, hemiSky: 0x7a6e88, hemiGround: 0x30282a, hemiI: 0.96,  night: 0.3, mist: 0.25 },
    { t: 0.56, elev: -8,  top: 0x16203a, horizon: 0x36456a, light: 0x8a9cd4, lightI: 1.35, hemiSky: 0x6a7aa6, hemiGround: 0x2a2a36, hemiI: 1.42, night: 0.8, mist: 0.25 },
    { t: 0.64, elev: -30, top: 0x0a1228, horizon: 0x24324e, light: 0x9ab0f0, lightI: 1.46, hemiSky: 0x6a7caa, hemiGround: 0x2a2c38, hemiI: 1.68, night: 1, mist: 0.22 },
    { t: 0.86, elev: -30, top: 0x0a1228, horizon: 0x24324e, light: 0x9ab0f0, lightI: 1.46, hemiSky: 0x6a7caa, hemiGround: 0x2a2c38, hemiI: 1.68, night: 1, mist: 0.22 },
    { t: 0.94, elev: -6,  top: 0x1e2846, horizon: 0x5a5470, light: 0xc0a0a8, lightI: 1.3, hemiSky: 0x7a7ca0, hemiGround: 0x2a2a32, hemiI: 1.35, night: 0.6, mist: 0.35 },
  ],
  water: { shallow: 0x4e8478, deep: 0x15364a, foam: 0xd6e2de },
  flowers: {
    village: [0xe8e4d8, 0xd8bc5a, 0xb87a8a], downs: [0xd8bc5a, 0xe8e4d8, 0x8e80b0, 0xc0784e], birchwood: [0xe8e4d8, 0xd8cc94],
    lake: [0x7a96c4, 0xe8e4d8], copperback: [0xd8bc5a, 0xc08a4e], shore: [0xe8e4d8], fen: [0xc8c090], grove: [0x88c4bc, 0xe8e4d8], cove: [0xe8e4d8, 0x80c4b8], '*': [0xe8e4d8, 0xd8bc5a],
  },
  bush: 0x34542e, reed: 0x76784a, pebble: 0x86827a,
  grade: {
    sat: 0.88, contrast: 0.3, wrap: 0.14, shadowTint: [-0.02, 0.006, 0.035], highTint: [0.03, 0.014, -0.025], rim: 0.55,
    fogNear: 52, fogFar: 132, ground: { sat: 0.9, value: 0.92, cast: [0.94, 1.0, 0.82], patch: 0.09 },
  },
};

export const LOOKS = { moody: MOODY, classic: VALLEY } as const;
export type LookName = keyof typeof LOOKS;
const LOOK_KEY = 'sunderfen-look';
/** The look picked in settings (moody unless the player chose classic). Applies on reload. */
export function lookName(): LookName {
  try { if (localStorage.getItem(LOOK_KEY) === 'classic') return 'classic'; } catch { /* ignore */ }
  return 'moody';
}
export function setLookName(n: LookName) { try { localStorage.setItem(LOOK_KEY, n); } catch { /* ignore */ } }
export function activeLook(): Look { return LOOKS[lookName()]; }

/** Sample the sky table at time t (wraps around). */
export function sampleSky(look: Look, t: number) {
  const keys = look.sky;
  t = ((t % 1) + 1) % 1;
  let i = keys.length - 1;
  for (let k = 0; k < keys.length; k++) if (keys[k].t <= t) i = k;
  const a = keys[i], b = keys[(i + 1) % keys.length];
  const span = ((b.t - a.t + 1) % 1) || 1, f = (((t - a.t + 1) % 1) / span);
  const s = f * f * (3 - 2 * f);
  const mixHex = (x: number, y: number) => {
    const r = ((x >> 16) & 255) + ((((y >> 16) & 255) - ((x >> 16) & 255)) * s);
    const g = ((x >> 8) & 255) + ((((y >> 8) & 255) - ((x >> 8) & 255)) * s);
    const bl = (x & 255) + (((y & 255) - (x & 255)) * s);
    return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
  };
  const lerp = (x: number, y: number) => x + (y - x) * s;
  return {
    elev: lerp(a.elev, b.elev), top: mixHex(a.top, b.top), horizon: mixHex(a.horizon, b.horizon), light: mixHex(a.light, b.light), lightI: lerp(a.lightI, b.lightI),
    hemiSky: mixHex(a.hemiSky, b.hemiSky), hemiGround: mixHex(a.hemiGround, b.hemiGround), hemiI: lerp(a.hemiI, b.hemiI), night: lerp(a.night, b.night), mist: lerp(a.mist, b.mist),
  };
}

export function dayLabel(t: number) {
  t = ((t % 1) + 1) % 1;
  if (t < 0.04 || t >= 0.94) return 'Dawn';
  if (t < 0.18) return 'Morning';
  if (t < 0.32) return 'Midday';
  if (t < 0.44) return 'Afternoon';
  if (t < 0.53) return 'Evening';
  return 'Night';
}
